import React, { useMemo, useState } from "react";
import { Popover, PopoverBackdrop, PopoverPanel } from "@headlessui/react";
import {
  IdentifiableAnswerV3Input,
  LocalizedParticipantQuestion,
  QuestionPrepopulation,
} from "../../apis/API";
import { strings } from "../../resources/strings";
import { resolveLocalizedText } from "../../utils/questionUtils";
import WelcomeScreen from "./WelcomeScreen";
import WizardToolbar from "./WizardToolbar";
import WizardStepHeader from "./WizardStepHeader";
import QuestionDispatcher from "./flow/QuestionDispatcher";
import QuestionListMenu from "./QuestionListMenu";
import FinalStatementScreen from "./FinalStatementScreen";
import { useQuestionFlow } from "./flow/useQuestionFlow";

export interface NewQuestionsFormProps {
  participantQuestions: (LocalizedParticipantQuestion | null)[];
  /**
   * Submit the gathered answers (createParticipantAnswersV3). Resolves true on
   * success, false on failure; must not reject. The host assembles the rest of
   * the V3 payload (case/app/check-in context) around these answers.
   */
  onSubmit: (
    answers: IdentifiableAnswerV3Input[],
    meta: { signerName: string },
  ) => Promise<boolean>;
  /** Close/exit the wizard without submitting (toolbar ✕ / abandon). */
  onClose: () => void;
  /** Called after a successful submit — the completion close (marks the
   * check-in complete), distinct from `onClose`'s abandon semantics. */
  onSubmitted: () => void;
  /** Name of the agency question group, used as a category fallback. */
  groupName?: string | null;
  /** Whether the Question List menu should show section/group names (IHA-348). */
  displaySectionGroupNames?: boolean | null;
  /** Per-workflow prepopulation (on-file data) from `case.questionPrepopulation`. */
  questionPrepopulation?: (QuestionPrepopulation | null)[] | null;
}

/**
 * Derive the category label for the current question from its semantic group,
 * falling back to the group name and finally a default.
 */
const getCategoryLabel = (
  question: LocalizedParticipantQuestion | null | undefined,
  groupName: string | null | undefined,
): string => {
  const semanticText = resolveLocalizedText(question?.semanticGroup?.localization);
  return semanticText ?? groupName ?? strings.reportForm.getStarted;
};

/**
 * Host shell for the new multi-screen question forms wizard.
 *
 * Renders the persistent toolbar on every screen, the Welcome screen before
 * question 1, then the flow engine (IHA-347) which sequences the question
 * components and accumulates answers. When every supported question is answered
 * it shows the Final Statement screen, which submits via `onSubmit` (IHA-349).
 */
const NewQuestionsForm: React.FC<NewQuestionsFormProps> = ({
  participantQuestions,
  onSubmit,
  onClose,
  onSubmitted,
  groupName,
  displaySectionGroupNames,
  questionPrepopulation,
}) => {
  const [started, setStarted] = useState<boolean>(false);
  const flow = useQuestionFlow(participantQuestions, {
    displaySectionGroupNames,
  });

  // Index prepopulation by workflowName so the current workflow question gets its
  // on-file `values` (the wizard renders one question at a time).
  const prepopByWorkflow = useMemo(() => {
    const map = new Map<string, string>();
    for (const entry of questionPrepopulation ?? []) {
      if (entry?.workflowName && entry.values != null) {
        map.set(entry.workflowName, entry.values);
      }
    }
    return map;
  }, [questionPrepopulation]);

  // Never leave the Welcome screen when there's nothing to answer.
  const handleStart = () => {
    if (flow.total > 0) {
      setStarted(true);
    }
  };

  // Jump to a question from the menu: enter the flow (in case it was opened from
  // Welcome). Flow state (answers, index) is untouched here; the Popover's
  // close() (passed at the call site) dismisses the menu.
  const handleSelectQuestion = (index: number) => {
    flow.goTo(index);
    setStarted(true);
  };

  return (
    <Popover
      as="div"
      data-testid="new-questions-form"
      // Bounded, keyboard-safe layout (IHA-350): cap the wizard height so the
      // toolbar stays fixed and the screen body scrolls, keeping a focused field
      // visible above the iOS keyboard. `dvh` shrinks with the keyboard. The
      // `-6rem` leaves room for the host modal panel's p-5 + m-5 so the whole
      // modal fits the viewport (no outer double-scroll). Scoped to this flag-ON
      // subtree — the shared host modal itself is untouched.
      className="flex max-h-[calc(100dvh-6rem)] w-full flex-col"
    >
      <WizardToolbar onClose={onClose} questionListDisabled={flow.total === 0} />

      {/* Dims the wizard behind the open menu (restores the backdrop the old
          Dialog provided). Sits just below the panel's z-index; clicking it
          closes the menu. */}
      <PopoverBackdrop className="fixed inset-0 z-[55] bg-black/40" />

      {/* Anchored to the toolbar's Question List button (IHA-387). `anchor`
          force-portals the panel (needs an explicit z-index above the wizard's
          own modal backdrop) and injects an inline `max-width` sized to the gap
          from the button to the viewport edge — that inline style overrides any
          Tailwind `max-w-*` class, so the menu width is set with an explicit
          `w-[36rem]`. The panel is free to be wider than the wizard modal
          behind it; question text wraps rather than truncating. floating-ui's
          injected max-width still shrinks it to fit on narrow screens.

          The card chrome (rounded/bg/shadow) lives here because floating-ui also
          injects a max-height on this element. Making it a flex column lets the
          menu's question list (flex-1) be the only scroller, so the panel itself
          never overflows and overflow-hidden clips the rounded corners. */}
      <PopoverPanel
        anchor="bottom start"
        aria-label={strings.reportForm.questionMenu.title}
        className="z-[60] flex w-[min(36rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
      >
        {({ close }) => (
          <QuestionListMenu
            questions={flow.questions}
            currentIndex={flow.index}
            answerFor={flow.answerFor}
            displaySectionGroupNames={flow.displaySectionGroupNames}
            onSelect={(index) => {
              handleSelectQuestion(index);
              close();
            }}
          />
        )}
      </PopoverPanel>

      {/* Scrollable screen body (IHA-350): the toolbar above stays fixed while
          this region scrolls, so a focused input can scroll clear of the iOS
          keyboard. min-h-0 lets the flex child actually shrink and scroll. */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {!started ? (
          <WelcomeScreen onStart={handleStart} />
        ) : flow.current ? (
          <>
            <WizardStepHeader
              category={getCategoryLabel(flow.current, groupName)}
              step={flow.step}
              total={flow.total}
            />
            <div className="mt-6">
              {/*
                Key on the flow's instanceKey, which bumps on every navigation
                (advance, back, and re-selecting the SAME question from the menu).
                Components seed their state from `defaultAnswer` once via useState,
                so the remount is what makes a same-type neighbor not inherit the
                prior question's state and makes re-entering a question (the only
                Figma back/change affordance) reset it to its first screen.
              */}
              <QuestionDispatcher
                key={flow.instanceKey}
                question={flow.current}
                defaultAnswer={flow.answerFor(flow.current.id ?? "")}
                prepopulationValues={prepopByWorkflow.get(
                  flow.current.workflowName ?? "",
                )}
                onAnswer={flow.recordAnswer}
              />
            </div>
          </>
        ) : (
          // All supported questions answered — Final Statement + submission.
          <FinalStatementScreen
            onSubmit={(signerName) => onSubmit(flow.answers, { signerName })}
            onComplete={onSubmitted}
          />
        )}
      </div>
    </Popover>
  );
};

export default NewQuestionsForm;
